import { useEffect, useMemo, useRef, useState } from 'react';
import { api, apiUrl, fmt, fmtDate, getToken, getUser, toDateInput } from '../api';
import { Modal, Field, Input, useToast, Loading, Empty, ErrorBox, Eyebrow } from '../ui';
import { Icons } from '../icons';

/**
 * Buxgalteriya — berilgan shartnomalar va qoldiq.
 *
 * Ikki varaq:
 *   «Qoldiq»       — mahsulot kesimida: rastamojkadan kelgan, shartnomaga
 *                    berilgan, qolgan. Manfiy qoldiq qizil: kelganidan ko'proq
 *                    shartnoma berilgan degani va buxgalter buni ko'rishi kerak.
 *   «Shartnomalar» — buxgalter kiritgan hujjatlar, tovar qatorlari bilan.
 *
 * Kirim tomoni bu yerda kiritilmaydi: u tasdiqlangan rastamojkadan avtomatik
 * keladi. GTD'siz tovar hisobga umuman tushmaydi — shartnomani faqat
 * rasmiylashtirilgan tovarga berish mumkin.
 */

type Line = { productId: number | ''; article?: string; name?: string; qty: string; priceUzs: string };

const emptyLine = (): Line => ({ productId: '', qty: '', priceUzs: '' });

export default function Accounting() {
  const user = getUser();
  const canEdit = ['admin', 'buxgalter'].includes(user?.role);
  const [tab, setTab] = useState<'balance' | 'stock' | 'contracts'>('balance');

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Buxgalteriya</h1>
          <p className="page-sub">
            Rasmiy (soliq bazasidagi) qoldiq — real ombor qoldig'i emas. Mijoz shartnoma
            so'raganda qancha berish mumkinligini shu yerdan ko'rasiz.
          </p>
        </div>
      </div>

      <div className="tabs">
        <button className={'tab' + (tab === 'balance' ? ' active' : '')} onClick={() => setTab('balance')}>Qoldiq</button>
        <button className={'tab' + (tab === 'stock' ? ' active' : '')} onClick={() => setTab('stock')}>Rasmiy kirim</button>
        <button className={'tab' + (tab === 'contracts' ? ' active' : '')} onClick={() => setTab('contracts')}>Berilgan shartnomalar</button>
      </div>

      {tab === 'balance' && <BalanceTab />}
      {tab === 'stock' && <StockTab canEdit={canEdit} />}
      {tab === 'contracts' && <ContractsTab canEdit={canEdit} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────── qoldiq

function BalanceTab() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlyNegative, setOnlyNegative] = useState(false);
  const [onlyUsed, setOnlyUsed] = useState(false);
  const toast = useToast();

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set('search', search.trim());
    if (onlyNegative) p.set('onlyNegative', 'true');
    if (onlyUsed) p.set('onlyUsed', 'true');
    return p.toString();
  }, [search, onlyNegative, onlyUsed]);

  const load = () => {
    setLoading(true); setErr(null);
    api.get('/contracts/balance' + (qs ? '?' + qs : ''))
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setErr(e.message); setLoading(false); });
  };

  // Qidiruv har harfda so'rov yubormasin
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [qs]);

  const exportExcel = () => {
    fetch(apiUrl('/contracts/balance/export' + (qs ? '?' + qs : '')), {
      headers: { Authorization: 'Bearer ' + getToken() },
    })
      .then((r) => { if (!r.ok) throw new Error('Eksport qilib bo\'lmadi'); return r.blob(); })
      .then((b) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = 'buxgalteriya-qoldiq.xlsx';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => toast(e.message, true));
  };

  const t = data?.totals;

  return (
    <div>
      <div className="filters">
        <input className="input" style={{ width: 280 }} placeholder="Artikul yoki nomi..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="chip" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} />
          {' '}Shartnoma berilganlari
        </label>
        <label className="chip" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyNegative} onChange={(e) => setOnlyNegative(e.target.checked)} />
          {' '}Faqat manfiy qoldiq
        </label>
        <button className="btn" onClick={exportExcel} disabled={!data?.rows?.length}>
          <Icons.download size={16} /> Excel
        </button>
      </div>

      {t && (
        <div className="stat-grid" style={{ marginBottom: 14 }}>
          <div className="stat">
            <div className="k">Kelgan</div>
            <div className="v">{fmt(t.inSum)}</div>
            <div className="d">so'm · {fmt(t.inQty, 2)} dona</div>
          </div>
          <div className="stat">
            <div className="k">Shartnomaga berilgan</div>
            <div className="v">{fmt(t.outSum)}</div>
            <div className="d">so'm · {fmt(t.outQty, 2)} dona</div>
          </div>
          <div className={'stat' + (t.leftSum < 0 ? ' danger' : ' ok')}>
            <div className="k">Qoldiq</div>
            <div className="v">{fmt(t.leftSum)}</div>
            <div className="d">so'm · {fmt(t.leftQty, 2)} dona</div>
          </div>
          <div className="stat">
            <div className="k">Mahsulot</div>
            <div className="v">{fmt(t.products)}</div>
            <div className="d">hisobdagi pozitsiya</div>
          </div>
        </div>
      )}

      {loading && !data ? <Loading /> : err ? <ErrorBox text={err} onRetry={load} />
        : !data?.rows?.length ? <Empty text="Ma'lumot yo'q" hint="«Rasmiy kirim» varag'iga yozuv kiriting — qoldiq shundan hisoblanadi." />
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Artikul</th><th>Nomi</th>
                  <th className="num">Kelgan (dona)</th><th className="num">Kelgan (so'm)</th>
                  <th className="num">Berilgan (dona)</th><th className="num">Berilgan (so'm)</th>
                  <th className="num">Qoldiq (dona)</th><th className="num">Qoldiq (so'm)</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r: any) => {
                  const neg = r.leftQty < 0 || r.leftSum < 0;
                  const red = neg ? { color: 'var(--danger)', fontWeight: 600 } : undefined;
                  return (
                    <tr key={r.productId}>
                      <td><span className="mono">{r.article || '—'}</span></td>
                      <td>{r.name || '—'}</td>
                      <td className="num">{fmt(r.inQty, 2)}</td>
                      <td className="num">{fmt(r.inSum)}</td>
                      <td className="num">{r.outQty ? fmt(r.outQty, 2) : '—'}</td>
                      <td className="num">{r.outSum ? fmt(r.outSum) : '—'}</td>
                      <td className="num" style={red}>{fmt(r.leftQty, 2)}</td>
                      <td className="num" style={red}>{fmt(r.leftSum)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={2}>JAMI</th>
                  <th className="num">{fmt(t.inQty, 2)}</th><th className="num">{fmt(t.inSum)}</th>
                  <th className="num">{fmt(t.outQty, 2)}</th><th className="num">{fmt(t.outSum)}</th>
                  <th className="num">{fmt(t.leftQty, 2)}</th><th className="num">{fmt(t.leftSum)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
    </div>
  );
}

// ─────────────────────────────────────────────────────── rasmiy kirim

/**
 * Rasmiy (soliq bazasidagi) kirim.
 *
 * <p>Bu jadval real ombor qoldig'iga ham, rastamojka moduliga ham bog'lanmagan
 * va bog'lanmasligi kerak: soliq bazasidagi holat haqiqiy tovar harakatidan
 * farq qiladi. Buxgalter bu yerni o'zi yuritadi.
 */
function StockTab({ canEdit }: { canEdit: boolean }) {
  const [list, setList] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<any>(null);
  const [imp, setImp] = useState(false);
  const toast = useToast();

  const load = () => {
    setErr(null);
    const p = new URLSearchParams();
    if (search.trim()) p.set('search', search.trim());
    api.get('/contracts/stock' + (p.toString() ? '?' + p.toString() : ''))
      .then(setList)
      .catch((e) => { setErr(e.message); setList([]); });
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);

  const remove = async (r: any) => {
    if (!confirm(`${r.product?.article} — ${fmt(r.qty, 2)} dona yozuvi o'chirilsinmi? Qoldiq qayta hisoblanadi.`)) return;
    try { await api.del('/contracts/stock/' + r.id); toast("O'chirildi"); load(); }
    catch (e: any) { toast(e.message, true); }
  };

  const totals = (list || []).reduce(
    (t, r) => ({ qty: t.qty + Number(r.qty || 0), sum: t.sum + Number(r.sumUzs || 0) }),
    { qty: 0, sum: 0 });

  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        Soliq bazasida nima turgan bo'lsa, shu yerga kiritiladi. Ombor qoldig'i va
        rastamojka moduli bu raqamlarga ta'sir qilmaydi.
      </p>

      <div className="filters">
        <input className="input" style={{ width: 280 }} placeholder="Artikul yoki nomi..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {canEdit && (
          <>
            <button className="btn btn-primary" onClick={() => setEdit({ date: toDateInput(new Date()) })}>
              <Icons.plus size={16} /> Kirim kiritish
            </button>
            <button className="btn" onClick={() => setImp(true)}>
              <Icons.upload size={16} /> Excel'dan yuklash
            </button>
          </>
        )}
      </div>

      {list && list.length > 0 && (
        <div className="stat-grid" style={{ marginBottom: 14 }}>
          <div className="stat">
            <div className="k">Yozuv</div>
            <div className="v">{fmt(list.length)}</div>
          </div>
          <div className="stat">
            <div className="k">Jami miqdor</div>
            <div className="v">{fmt(totals.qty, 2)}</div>
            <div className="d">dona</div>
          </div>
          <div className="stat">
            <div className="k">Jami summa</div>
            <div className="v">{fmt(totals.sum)}</div>
            <div className="d">so'm</div>
          </div>
        </div>
      )}

      {!list ? <Loading /> : err ? <ErrorBox text={err} onRetry={load} />
        : !list.length ? <Empty text="Kirim yozuvi yo'q" hint={canEdit ? "Yuqoridagi tugma orqali kiriting yoki Excel'dan yuklang." : undefined} />
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Sana</th><th>Artikul</th><th>Nomi</th><th>Hujjat</th>
                  <th className="num">Miqdor</th><th className="num">Narx</th><th className="num">Summa</th>
                  <th>Izoh</th>
                  {canEdit && <th style={{ width: 90 }}></th>}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.date)}</td>
                    <td><span className="mono">{r.product?.article}</span></td>
                    <td>{r.product?.name}</td>
                    <td className="muted">{r.docNumber || '—'}</td>
                    <td className="num">{fmt(r.qty, 2)}</td>
                    <td className="num">{fmt(r.priceUzs)}</td>
                    <td className="num">{fmt(r.sumUzs)}</td>
                    <td className="muted">{r.note || '—'}</td>
                    {canEdit && (
                      <td>
                        <div className="act-row">
                          <button className="icon-btn" title="Tahrirlash" onClick={() => setEdit({
                            id: r.id, article: r.product?.article, qty: String(r.qty),
                            priceUzs: String(r.priceUzs), date: toDateInput(r.date),
                            docNumber: r.docNumber || '', note: r.note || '',
                          })}><Icons.edit size={16} /></button>
                          <button className="icon-btn" title="O'chirish" onClick={() => remove(r)}><Icons.trash size={16} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {edit && <StockModal value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {imp && <StockImportModal onClose={() => setImp(false)} onDone={() => { setImp(false); load(); }} />}
    </div>
  );
}

function StockModal({ value, onClose, onSaved }: { value: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>(value);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const qty = parseFloat(form.qty || '0') || 0;
  const price = parseFloat(form.priceUzs || '0') || 0;

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.patch('/contracts/stock/' + form.id, form);
      else await api.post('/contracts/stock', form);
      toast(form.id ? 'Yangilandi' : 'Kirim qo\'shildi');
      onSaved();
    } catch (e: any) { toast(e.message, true); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={form.id ? 'Kirimni tahrirlash' : 'Rasmiy kirim'} onClose={onClose}>
      <div className="grid-2">
        <Field label="Artikul *">
          <Input value={form.article || ''} onChange={(e) => setForm({ ...form, article: e.target.value })} placeholder="14-1-15-0005" />
        </Field>
        <Field label="Sana *">
          <Input type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
      </div>
      <div className="grid-2">
        <Field label="Miqdor *">
          <Input className="num" value={form.qty || ''} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
        </Field>
        <Field label="Dona narxi, so'm *">
          <Input className="num" value={form.priceUzs || ''} onChange={(e) => setForm({ ...form, priceUzs: e.target.value })} />
        </Field>
      </div>
      <div className="grid-2">
        <Field label="Hujjat (GTD, invoys)">
          <Input value={form.docNumber || ''} onChange={(e) => setForm({ ...form, docNumber: e.target.value })} />
        </Field>
        <Field label="Izoh">
          <Input value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>
      <div className="card" style={{ padding: 10, marginTop: 4 }}>
        <span className="muted">Summa: </span><b>{fmt(qty * price)}</b> <span className="muted">so'm</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose}>Bekor</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !form.article || !(qty > 0) || !(price > 0)}>
          {saving ? 'Saqlanmoqda…' : 'Saqlash'}
        </button>
      </div>
    </Modal>
  );
}

function StockImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const send = async (f: File, dryRun: boolean) => {
    setBusy(true);
    try {
      const r = await api.upload('/contracts/stock/import', f, { dryRun: String(dryRun) });
      if (dryRun) { setFile(f); setPreview(r); }
      else { toast(`${r.willWrite} ta yozuv qo'shildi`); onDone(); }
    } catch (e: any) { toast(e.message, true); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Rasmiy kirimni Excel'dan yuklash" onClose={onClose} large>
      <p className="muted" style={{ marginTop: 0 }}>
        Ustunlar: <b>Artikul · Miqdor · Narx · Sana · Hujjat · Izoh</b>. Birinchi qator sarlavha.
        Narx o'rniga faqat <b>Summa</b> bo'lsa, dona narxi undan hisoblanadi.
      </p>
      <div className="filters" style={{ marginBottom: 10 }}>
        <input ref={fileRef} type="file" accept=".xlsx" hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) send(f, true); if (fileRef.current) fileRef.current.value = ''; }} />
        <button className="btn btn-primary" disabled={busy} onClick={() => fileRef.current?.click()}>
          <Icons.upload size={16} /> {preview ? 'Boshqa fayl' : 'Faylni tanlash'}
        </button>
        <button className="btn" onClick={() => api.download('/contracts/stock/template', 'rasmiy-kirim-namuna.xlsx')}>
          <Icons.download size={16} /> Namuna fayl
        </button>
      </div>

      {busy && <Loading rows={2} />}

      {preview && !busy && (
        <div className="card" style={{ padding: 12 }}>
          <Eyebrow title="Nima yoziladi" desc={`${preview.willWrite} ta yozuv · ${fmt(preview.totalQty, 2)} dona · ${fmt(preview.totalSum)} so'm`} />
          {!!preview.errors?.length && (
            <div style={{ marginTop: 8, maxHeight: 140, overflowY: 'auto' }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                Tashlab yuborilgan qatorlar ({preview.errors.length} ta):
              </div>
              {preview.errors.slice(0, 40).map((e: any, i: number) => (
                <div key={i} className="muted" style={{ fontSize: 12 }}>{e.row}-qator: {e.reason}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose}>Bekor</button>
        <button className="btn btn-primary" disabled={busy || !file || !preview?.willWrite}
          onClick={() => file && send(file, false)}>
          {busy ? 'Yuklanmoqda…' : `Tasdiqlash va saqlash (${preview?.willWrite ?? 0})`}
        </button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────── shartnomalar

function ContractsTab({ canEdit }: { canEdit: boolean }) {
  const [list, setList] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<any>(null);
  const [imp, setImp] = useState(false);
  const toast = useToast();

  const load = () => {
    setLoading(true); setErr(null);
    const p = new URLSearchParams();
    if (search.trim()) p.set('search', search.trim());
    api.get('/contracts' + (p.toString() ? '?' + p.toString() : ''))
      .then((d) => { setList(d); setLoading(false); })
      .catch((e) => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);

  const remove = async (c: any) => {
    if (!confirm(`«${c.number}» shartnomasi o'chirilsinmi? Qoldiq shu zahoti qayta hisoblanadi.`)) return;
    try { await api.del('/contracts/' + c.id); toast('Shartnoma o\'chirildi'); load(); }
    catch (e: any) { toast(e.message, true); }
  };

  const sumOf = (c: any) => (c.items || []).reduce((s: number, i: any) => s + Number(i.sumUzs || 0), 0);

  return (
    <div>
      <div className="filters">
        <input className="input" style={{ width: 280 }} placeholder="Raqam yoki mijoz..."
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {canEdit && (
          <>
            <button className="btn btn-primary" onClick={() => setEdit({ date: toDateInput(new Date()), items: [emptyLine()] })}>
              <Icons.plus size={16} /> Shartnoma kiritish
            </button>
            <button className="btn" onClick={() => setImp(true)}>
              <Icons.upload size={16} /> Excel'dan yuklash
            </button>
          </>
        )}
      </div>

      {loading && !list ? <Loading /> : err ? <ErrorBox text={err} onRetry={load} />
        : !list?.length ? <Empty text="Shartnoma yo'q" hint={canEdit ? 'Yuqoridagi tugma orqali kiriting.' : undefined} />
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Raqam</th><th>Sana</th><th>Mijoz</th>
                  <th className="num">Qator</th><th className="num">Summa (so'm)</th><th>Izoh</th>
                  {canEdit && <th style={{ width: 90 }}></th>}
                </tr>
              </thead>
              <tbody>
                {list.map((c: any) => (
                  <tr key={c.id}>
                    <td><span className="mono">{c.number}</span></td>
                    <td>{fmtDate(c.date)}</td>
                    <td>{c.customerName || '—'}</td>
                    <td className="num">{c.items?.length ?? 0}</td>
                    <td className="num">{fmt(sumOf(c))}</td>
                    <td className="muted">{c.note || '—'}</td>
                    {canEdit && (
                      <td>
                        <div className="act-row">
                          <button className="icon-btn" title="Tahrirlash" onClick={() => setEdit({
                            ...c,
                            date: toDateInput(c.date),
                            items: (c.items || []).map((i: any) => ({
                              productId: i.productId, article: i.product?.article, name: i.product?.name,
                              qty: String(i.qty), priceUzs: String(i.priceUzs),
                            })),
                          })}><Icons.edit size={16} /></button>
                          <button className="icon-btn" title="O'chirish" onClick={() => remove(c)}><Icons.trash size={16} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {edit && <ContractModal value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {imp && <ImportModal onClose={() => setImp(false)} onDone={() => { setImp(false); load(); }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────── Excel'dan yuklash

const STATUS: Record<string, { label: string; color: string }> = {
  yangi: { label: 'yangi', color: 'var(--ok)' },
  qisman: { label: 'qisman', color: 'var(--warn)' },
  mavjud: { label: "o'tkazib yuboriladi", color: 'var(--muted)' },
  xato: { label: 'xato', color: 'var(--danger)' },
};

/**
 * Excel'dan shartnomalarni yuklash.
 *
 * Ikki qadam: avval fayl `dryRun` bilan yuboriladi va NIMA yoziladi shu yerda
 * ko'rinadi, keyingina saqlanadi. Bu moliyaviy hujjat — noto'g'ri faylni
 * jimgina bazaga yozib yuborish mumkin emas.
 */
function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const send = async (f: File, dryRun: boolean) => {
    setBusy(true);
    try {
      const r = await api.upload('/contracts/import', f, { dryRun: String(dryRun) });
      if (dryRun) { setFile(f); setPreview(r); }
      else {
        toast(`${r.added} ta shartnoma saqlandi` + (r.skipped ? `, ${r.skipped} tasi o'tkazib yuborildi` : ''));
        onDone();
      }
    } catch (e: any) { toast(e.message, true); }
    finally { setBusy(false); }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) send(f, true);
    if (fileRef.current) fileRef.current.value = '';
  };

  const willAdd = (preview?.contracts || []).filter((c: any) => c.status === 'yangi' || c.status === 'qisman').length;

  return (
    <Modal title="Excel'dan shartnoma yuklash" onClose={onClose} large>
      <p className="muted" style={{ marginTop: 0 }}>
        Ustunlar: <b>Shartnoma raqami</b>, <b>Sana</b>, <b>Mijoz</b>, <b>Artikul</b>, <b>Miqdor</b>, <b>Narx</b>, <b>Izoh</b>.
        Birinchi qator — sarlavha. Bir xil raqamli qatorlar bitta shartnomaga yig'iladi,
        ya'ni bitta faylda bir nechta shartnoma bo'lishi mumkin. Narx o'rniga <b>Summa</b> bo'lsa,
        dona narxi undan hisoblanadi.
      </p>

      <div className="filters" style={{ marginBottom: 12 }}>
        <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onPick} />
        <button className="btn btn-primary" disabled={busy} onClick={() => fileRef.current?.click()}>
          <Icons.upload size={16} /> {preview ? 'Boshqa fayl' : 'Faylni tanlash'}
        </button>
        <button className="btn" onClick={() => api.download('/contracts/import/template', 'shartnoma-namuna.xlsx')}>
          <Icons.download size={16} /> Namuna fayl
        </button>
      </div>

      {busy && <Loading rows={3} />}

      {preview && !busy && (
        <>
          <Eyebrow title="Nima yoziladi" desc={`${willAdd} ta shartnoma saqlanadi, ${preview.contracts.length - willAdd} tasi o'tkazib yuboriladi`} />
          <div className="table-wrap" style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Raqam</th><th>Sana</th><th>Mijoz</th>
                  <th className="num">Qator</th><th className="num">Dona</th><th className="num">Summa</th><th>Holat</th>
                </tr>
              </thead>
              <tbody>
                {preview.contracts.map((c: any, i: number) => {
                  const st = STATUS[c.status] || STATUS.xato;
                  return (
                    <tr key={i}>
                      <td><span className="mono">{c.number}</span></td>
                      <td>{fmtDate(c.date)}</td>
                      <td>{c.customerName || '—'}</td>
                      <td className="num">{c.lines}</td>
                      <td className="num">{fmt(c.qty, 2)}</td>
                      <td className="num">{fmt(c.sum)}</td>
                      <td style={{ color: st.color }} title={c.reason || ''}>{st.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!!preview.errors?.length && (
            <div style={{ marginTop: 10 }}>
              <Eyebrow title="Tashlab yuborilgan qatorlar" desc={`${preview.errors.length} ta`} />
              <div className="card" style={{ marginTop: 6, padding: 10, maxHeight: 140, overflowY: 'auto' }}>
                {preview.errors.slice(0, 50).map((e: any, i: number) => (
                  <div key={i} className="muted" style={{ fontSize: 12 }}>{e.row}-qator: {e.reason}</div>
                ))}
                {preview.errors.length > 50 && <div className="muted" style={{ fontSize: 12 }}>…yana {preview.errors.length - 50} ta</div>}
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose}>Bekor</button>
        <button className="btn btn-primary" disabled={busy || !file || !willAdd}
          onClick={() => file && send(file, false)}>
          {busy ? 'Yuklanmoqda...' : `Tasdiqlash va saqlash (${willAdd})`}
        </button>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────── kiritish oynasi

function ContractModal({ value, onClose, onSaved }: { value: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>(value);
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState<Record<number, any>>({});
  const toast = useToast();

  // Qoldiq — qator yonida «shundan qancha qolgan» ni ko'rsatish va narxni
  // taklif qilish uchun. Kelmasa oyna baribir ishlayveradi.
  useEffect(() => {
    api.get('/contracts/balance').then((d) => {
      const map: Record<number, any> = {};
      for (const r of d.rows || []) map[r.productId] = r;
      setBalance(map);
    }).catch(() => setBalance({}));
  }, []);

  const setLine = (idx: number, patch: Partial<Line>) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, items });
  };
  const addLine = () => setForm({ ...form, items: [...form.items, emptyLine()] });
  const dropLine = (idx: number) => setForm({ ...form, items: form.items.filter((_: any, i: number) => i !== idx) });

  const total = form.items.reduce((s: number, l: Line) =>
    s + (parseFloat(l.qty || '0') || 0) * (parseFloat(l.priceUzs || '0') || 0), 0);

  const ready = form.number && form.date &&
    form.items.some((l: Line) => l.productId && parseFloat(l.qty || '0') > 0 && parseFloat(l.priceUzs || '0') > 0);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        number: form.number, date: form.date, customerName: form.customerName, note: form.note,
        items: form.items
          .filter((l: Line) => l.productId && parseFloat(l.qty || '0') > 0 && parseFloat(l.priceUzs || '0') > 0)
          .map((l: Line) => ({ productId: l.productId, qty: l.qty, priceUzs: l.priceUzs })),
      };
      if (form.id) await api.patch('/contracts/' + form.id, body);
      else await api.post('/contracts', body);
      toast(form.id ? 'Shartnoma yangilandi' : 'Shartnoma saqlandi');
      onSaved();
    } catch (e: any) { toast(e.message, true); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={form.id ? 'Shartnomani tahrirlash' : 'Yangi shartnoma'} onClose={onClose} large>
      <div className="grid-2">
        <Field label="Shartnoma raqami *">
          <Input value={form.number || ''} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="SH-2026-001" />
        </Field>
        <Field label="Sana *">
          <Input type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
      </div>
      <div className="grid-2">
        <Field label="Mijoz">
          <Input value={form.customerName || ''} onChange={(e) => setForm({ ...form, customerName: e.target.value })} placeholder="Mijoz nomi" />
        </Field>
        <Field label="Izoh">
          <Input value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10, marginTop: 6 }}>
        <Eyebrow title="Tovarlar" desc="Shartnomada ko'rsatilgan miqdor va dona narxi" />
        <button className="btn btn-sm" onClick={addLine}><Icons.plus size={14} /> Qator</button>
      </div>

      <div className="table-wrap" style={{ marginTop: 8 }}>
        <table>
          <thead>
            <tr>
              <th style={{ minWidth: 250 }}>Tovar</th>
              <th className="num" style={{ width: 100 }}>Miqdor</th>
              <th className="num" style={{ width: 130 }}>Narx (so'm)</th>
              <th className="num" style={{ width: 140 }}>Summa</th>
              <th className="num" style={{ width: 120 }}>Qoldiq (dona)</th>
              <th style={{ width: 44 }}></th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((l: Line, i: number) => {
              const b = l.productId ? balance[Number(l.productId)] : null;
              const qty = parseFloat(l.qty || '0') || 0;
              const price = parseFloat(l.priceUzs || '0') || 0;
              // Shu qator hisobga olingandan keyin nima qoladi — buxgalter
              // shartnomani saqlashdan OLDIN oshib ketganini ko'rsin.
              const after = b ? b.leftQty - qty : null;
              return (
                <tr key={i}>
                  <td>
                    <ProductPicker
                      value={l}
                      onPick={(p) => setLine(i, {
                        productId: p.id, article: p.article, name: p.name,
                        priceUzs: l.priceUzs || (balance[p.id]?.lastUnitPrice ? String(balance[p.id].lastUnitPrice) : ''),
                      })}
                    />
                  </td>
                  <td><Input className="num" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                  <td><Input className="num" value={l.priceUzs} onChange={(e) => setLine(i, { priceUzs: e.target.value })} /></td>
                  <td className="num">{fmt(qty * price)}</td>
                  <td className="num muted">
                    {after == null ? '—' : (
                      <span style={after < 0 ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>{fmt(after, 2)}</span>
                    )}
                  </td>
                  <td>
                    {form.items.length > 1 && (
                      <button className="icon-btn" title="Qatorni o'chirish" onClick={() => dropLine(i)}><Icons.trash size={15} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr><th colSpan={3}>JAMI</th><th className="num">{fmt(total)}</th><th colSpan={2}></th></tr>
          </tfoot>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose}>Bekor</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !ready}>
          {saving ? 'Saqlanmoqda...' : 'Saqlash'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Artikul yoki nom bo'yicha tovar tanlash.
 *
 * Zakaz sahifasidagi qidiruv bilan bir xil idiom: 2 harfdan boshlab
 * `/products?search=` so'raladi, natija ustidagi `card` ichida chiqadi.
 */
function ProductPicker({ value, onPick }: { value: Line; onPick: (p: any) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => {
      api.get('/products?pageSize=8&search=' + encodeURIComponent(q.trim()))
        .then((d) => setItems(d?.items || []))
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  if (value.productId && !open) {
    return (
      <div style={{ cursor: 'pointer' }} title="Almashtirish uchun bosing"
        onClick={() => { setOpen(true); setQ(''); }}>
        <div className="row-title">{value.name || '—'}</div>
        <div className="row-sub mono">{value.article || ''}</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Input autoFocus value={q} placeholder="Artikul yoki nom (2 harfdan)"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)} />
      {open && q.trim().length >= 2 && (
        <div className="card" style={{ position: 'absolute', top: 42, left: 0, right: 0, zIndex: 30, padding: 6, maxHeight: 280, overflowY: 'auto', minWidth: 320 }}>
          {items.length === 0 ? <div className="muted" style={{ padding: 10 }}>Topilmadi…</div> :
            items.map((p) => (
              <div key={p.id} className="row-card" style={{ margin: 4, padding: '8px 12px', cursor: 'pointer' }}
                onMouseDown={() => { onPick(p); setOpen(false); setQ(''); }}>
                <div>
                  <div className="row-title">{p.name}</div>
                  <div className="row-sub">{p.article}{p.brand?.name ? ' · ' + p.brand.name : ''}</div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
